import { useCallback, useEffect, useRef, useState } from "react";
import { CreateMLCEngine } from "@mlc-ai/web-llm";
import { QUANTY_SYSTEM_PROMPT } from "../config/prompt";
import type { QuantyResponse } from "../types";
import { EMOTION_CATALOG, type ReactionId } from "../lib/emotionCatalog";

const MODEL_ID = "Qwen3-0.6B-q4f16_1-MLC";

const REACTION_IDS = new Set<string>(EMOTION_CATALOG.map((entry) => entry.id));

const BASE_MODEL_LIST = [
  {
    model: "https://huggingface.co/mlc-ai/Qwen3-0.6B-q4f16_1-MLC",
    model_id: "Qwen3-0.6B-q4f16_1-MLC",
    model_lib:
      "https://raw.githubusercontent.com/mlc-ai/binary-mlc-llm-libs/main/web-llm-models/v0_2_80/Qwen3-0.6B-q4f16_1-ctx4k_cs1k-webgpu.wasm",
    vram_required_MB: 600,
    low_resource_required: true,
    overrides: {
      context_window_size: 4096,
    },
  },
];

function createEngineConfig(useIndexedDBCache: boolean) {
  return {
    appConfig: {
      useIndexedDBCache,
      model_list: BASE_MODEL_LIST.map((entry) => ({
        ...entry,
        overrides: { ...entry.overrides },
      })),
    },
  };
}

export function useQuantyLLM() {
  const engineRef = useRef<Awaited<ReturnType<typeof CreateMLCEngine>> | null>(
    null
  );
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("Booting Quanty...");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const supportsCacheAPI = typeof window !== "undefined" && typeof window.caches !== "undefined";
        const statusPrefix = supportsCacheAPI
          ? "Downloading weights..."
          : "Downloading weights... (browser cache unavailable; using slower fallback)";
        setStatus(statusPrefix);

        const engine = await CreateMLCEngine(MODEL_ID, createEngineConfig(!supportsCacheAPI));
        if (cancelled) {
          return;
        }
        engineRef.current = engine;
        setReady(true);
        const readyMessage = supportsCacheAPI
          ? "Quanty is awake."
          : "Quanty is awake. Browser cache API is unavailable, so loading may be slower.";
        setStatus(readyMessage);
      } catch (err) {
        console.error("Failed to init WebLLM", err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unknown WebLLM error");
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const sendPrompt = useCallback(
    async (
      history: Array<{ role: "user" | "assistant"; content: string }>,
      prompt: string
    ): Promise<QuantyResponse> => {
      if (!engineRef.current) {
        throw new Error("Quanty engine not ready yet");
      }

      const payload = await engineRef.current.chat.completions.create({
        model: MODEL_ID,
        messages: [
          { role: "system", content: QUANTY_SYSTEM_PROMPT },
          ...history,
          { role: "user", content: prompt },
        ],
        temperature: 0.2,
        max_tokens: 4000,
        stream: false,
      });

      const rawOutput = payload.choices?.[0]?.message?.content ?? "";
      const trimmedOutput = rawOutput.trim();
      const { thinking: extractedThinking, remainder } = splitThinking(trimmedOutput);
      const thinking = extractedThinking && extractedThinking.length > 0 ? extractedThinking : null;

      let resolution = resolveReaction(remainder);
      if (!resolution.reaction) {
        const fallbackResolution = resolveReaction(trimmedOutput);
        if (fallbackResolution.reaction) {
          resolution = fallbackResolution;
        }
      }

      let reaction = resolution.reaction;
      if (!reaction) {
        console.warn("Quanty fallback: unable to parse reaction, defaulting to confused", trimmedOutput);
        reaction = "confused";
      } else if (resolution.reason && resolution.reason !== "json" && resolution.reason !== "json-embedded") {
        console.warn("Quanty fallback parsed reaction heuristically", {
          output: trimmedOutput,
          reaction,
          reason: resolution.reason,
        });
      }

      const normalizedContent = normalizeHistoryContent(trimmedOutput, reaction, thinking);

      return {
        reaction,
        raw: normalizedContent,
        thinking,
      };
    },
    []
  );

  return { ready, status, error, sendPrompt };
}

type ReactionResolutionReason = "json" | "json-embedded" | "heuristic" | null;

type ReactionResolution = {
  reaction: ReactionId | null;
  reason: ReactionResolutionReason;
};

type ReactionPayload = {
  reaction?: string;
};

function splitThinking(output: string): { thinking: string | null; remainder: string } {
  const thinkRegex = /<think>([\s\S]*?)<\/think>/i;
  const match = thinkRegex.exec(output);
  if (!match) {
    return { thinking: null, remainder: output };
  }

  const before = output.slice(0, match.index ?? 0);
  const after = output.slice((match.index ?? 0) + match[0].length);
  return {
    thinking: match[1].trim(),
    remainder: `${before}${after}`.trim(),
  };
}

function resolveReaction(text: string): ReactionResolution {
  const trimmed = text.trim();
  if (!trimmed) {
    return { reaction: null, reason: null };
  }

  const direct = tryParseReactionFromJson(trimmed);
  if (direct) {
    return { reaction: direct, reason: "json" };
  }

  const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    const embedded = tryParseReactionFromJson(jsonMatch[0]);
    if (embedded) {
      return { reaction: embedded, reason: "json-embedded" };
    }
  }

  const heuristic = findReactionIdByHeuristic(trimmed);
  if (heuristic) {
    return { reaction: heuristic, reason: "heuristic" };
  }

  return { reaction: null, reason: null };
}

function tryParseReactionFromJson(text: string): ReactionId | null {
  if (!text) {
    return null;
  }

  try {
    const parsed = JSON.parse(text) as ReactionPayload;
    return normalizeReactionToken(parsed.reaction ?? "");
  } catch (_error) {
    return null;
  }
}

function findReactionIdByHeuristic(text: string): ReactionId | null {
  const jsonLike = text.match(/"reaction"\s*:\s*"([a-z_]+)"/i);
  const fromJsonLike = normalizeReactionToken(jsonLike?.[1] ?? "");
  if (fromJsonLike) {
    return fromJsonLike;
  }

  const quoted = text.match(/"([a-z_]+)"/i);
  const fromQuoted = normalizeReactionToken(quoted?.[1] ?? "");
  if (fromQuoted) {
    return fromQuoted;
  }

  const tokens = text.split(/[\s,;:.]+/);
  for (const token of tokens) {
    const candidate = normalizeReactionToken(token);
    if (candidate) {
      return candidate;
    }
  }

  return null;
}

function normalizeReactionToken(value: string): ReactionId | null {
  const cleaned = value.trim().replace(/[^a-z_]/gi, "").toLowerCase();
  if (cleaned.length === 0) {
    return null;
  }
  if (REACTION_IDS.has(cleaned)) {
    return cleaned as ReactionId;
  }
  return null;
}

function normalizeHistoryContent(raw: string, reaction: ReactionId, thinking: string | null): string {
  const trimmedRaw = raw.trim();
  const hasThinkTag = /<think>[\s\S]*<\/think>/i.test(trimmedRaw);
  const hasReactionJson = /"reaction"\s*:/i.test(trimmedRaw);

  let result = trimmedRaw;

  if (thinking && thinking.length > 0 && !hasThinkTag) {
    const thinkBlock = `<think>${thinking}</think>`;
    result = result.length > 0 ? `${thinkBlock}\n${result}` : thinkBlock;
  }

  if (!hasReactionJson) {
    const reactionJson = JSON.stringify({ reaction });
    result = result.length > 0 ? `${result}${result.endsWith("\n") ? "" : "\n"}${reactionJson}` : reactionJson;
  }

  const normalized = result.trim();
  if (normalized.length === 0) {
    return JSON.stringify({ reaction });
  }
  return normalized;
}
